import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../../shared/layouts/MainLayout';
import { Card, Alert, Badge, Loading, Spinner } from '../../../shared/components';
import { useReports } from '../hooks/useReports';
import { useExport } from '../hooks/useExport';
import ExportButtons from '../components/ExportButtons';
import ReportTable from '../components/ReportTable';
import ReportCharts from '../components/ReportCharts';
import ReportFilters from '../components/ReportFilters';

const ExpiryReportPage = () => {
  const [filters, setFilters] = useState({
    expiryRange: 'all',
    startDate: '',
    endDate: ''
  });
  
  const { data, loading, error, refetch } = useReports('expiry', filters);
  const exportUtils = useExport();

  // Debug: Log the actual data structure
  useEffect(() => {
    console.log('ExpiryReportPage - Full data from API:', data);
    if (data) {
      console.log('ExpiryReportPage - data.data:', data.data);
      console.log('ExpiryReportPage - data.data.data:', data.data?.data);
      console.log('ExpiryReportPage - Is data.data.data an array?', Array.isArray(data.data?.data));
    }
  }, [data]);

  const getExpiryStatus = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'Expired', variant: 'danger', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'Critical', variant: 'danger', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 90) {
      return { status: 'Warning', variant: 'warning', days: daysUntilExpiry };
    }
    return { status: 'Good', variant: 'success', days: daysUntilExpiry };
  };

  const columns = [
    { header: 'Medicine Name', accessor: 'name' },
    { header: 'Batch Number', accessor: 'batch_number' },
    { 
      header: 'Expiry Date', 
      accessor: 'expiry_date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    { header: 'Quantity Available', accessor: 'quantity_available' },
    { 
      header: 'Status', 
      accessor: 'status',
      render: (value, row) => {
        const { status, variant, days } = getExpiryStatus(row.expiry_date);
        return (
          <div className="flex items-center">
            <Badge variant={variant}>{status}</Badge>
            {days <= 90 && (
              <span className="text-xs ml-2 text-gray-500">
                ({days} days)
              </span>
            )}
          </div>
        );
      }
    },
    {
      header: 'Action',
      accessor: 'action',
      render: (value, row) => {
        const { status } = getExpiryStatus(row.expiry_date);
        if (status === 'Expired') {
          return <Badge variant="danger">Dispose Required</Badge>;
        } else if (status === 'Critical') {
          return <Badge variant="warning">Priority Usage</Badge>;
        }
        return '-';
      }
    }
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    refetch();
  };

  const handleReset = () => {
    setFilters({
      expiryRange: 'all',
      startDate: '',
      endDate: ''
    });
  };

  // Extract the actual data array from the nested structure
  const getRawDataArray = () => {
    if (!data) return [];
    
    // The actual data is at data.data.data (third level)
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    
    // Fallback: try data.data if it's an array
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback: try data itself if it's an array
    if (Array.isArray(data)) {
      return data;
    }
    
    console.warn('Could not extract array from data:', data);
    return [];
  };

  const rawData = getRawDataArray();
  
  // Log what we got
  useEffect(() => {
    console.log('Extracted rawData:', rawData);
    console.log('Is rawData an array?', Array.isArray(rawData));
    console.log('rawData length:', rawData.length);
  }, [rawData]);

  // Filter data based on expiry range
  const filteredData = useMemo(() => {
    if (!rawData.length) return [];
    
    const today = new Date();
    return rawData.filter(item => {
      const expiryDate = new Date(item.expiry_date);
      const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      
      switch (filters.expiryRange) {
        case 'expired':
          return daysUntilExpiry < 0;
        case '30days':
          return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
        case '90days':
          return daysUntilExpiry > 30 && daysUntilExpiry <= 90;
        default:
          return true;
      }
    });
  }, [rawData, filters.expiryRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    const expired = rawData.filter(item => new Date(item.expiry_date) < new Date()).length;
    const critical = rawData.filter(item => {
      const days = Math.ceil((new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 30;
    }).length;
    const warning = rawData.filter(item => {
      const days = Math.ceil((new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
      return days > 30 && days <= 90;
    }).length;
    
    return { expired, critical, warning };
  }, [rawData]);

  // Handler functions for exports
  const handleCSVExport = () => {
    exportUtils.exportToCSV(filteredData, 'expiry_report');
  };

  const handleExcelExport = () => {
    exportUtils.exportToExcel(filteredData, 'expiry_report');
  };

  const handlePDFExport = () => {
    exportUtils.exportToPDF(
      filteredData, 
      columns, 
      'Medicine Expiry Report', 
      'expiry_report'
    );
  };

  const handlePrint = () => {
    exportUtils.print();
  };

  if (loading) return <Spinner />;
  if (error) return <Alert type="error" message={error} />;

  return (
    // <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Medicine Expiry Report</h1>
        
        <ReportFilters
          filters={filters}
          onApply={handleApplyFilters}
          onReset={handleReset}
        />
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Total Batches</h3>
              <p className="text-2xl font-bold">{rawData.length}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Expired</h3>
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Critical (≤30 days)</h3>
              <p className="text-2xl font-bold text-orange-500">{stats.critical}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Warning (≤90 days)</h3>
              <p className="text-2xl font-bold text-yellow-500">{stats.warning}</p>
            </div>
          </Card>
        </div>
        
        <ExportButtons
          data={{ data: filteredData }}
          columns={columns}
          filename="expiry_report"
          title="Medicine Expiry Report"
          onExportCSV={handleCSVExport}
          onExportExcel={handleExcelExport}
          onExportPDF={handlePDFExport}
          onPrint={handlePrint}
        />
        
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Expiry Details by Batch</h2>
            <ReportTable
              data={{ data: filteredData }}
              columns={columns}
            />
          </div>
        </Card>
        
        {filteredData.length > 0 && (
          <ReportCharts
            data={filteredData}
            type="expiry"
            title="Expiry Distribution"
          />
        )}

        {/* Debug section - remove in production */}
        {/* {process.env.NODE_ENV === 'development' && (
          <Card className="mt-4">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Info</h3>
              <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-40">
                {JSON.stringify({ 
                  dataReceived: !!data,
                  dataType: data ? typeof data : 'null',
                  hasDataProperty: data?.data ? 'yes' : 'no',
                  hasNestedData: data?.data?.data ? 'yes' : 'no',
                  isNestedDataArray: data?.data?.data ? Array.isArray(data.data.data) : 'n/a',
                  rawDataLength: rawData.length,
                  filteredDataLength: filteredData.length,
                  stats
                }, null, 2)}
              </pre>
            </div>
          </Card>
        )} */}
      </div>
    // </MainLayout>
  );
};

export default ExpiryReportPage;